import { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ExternalLink, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [configuredSystems, setConfiguredSystems] = useState([]);
  const [assignedAgentsMap, setAssignedAgentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticket_number: '',
    source: 'local',
    title: '',
    description: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    sap_system: 'S/4HANA Cloud',
    target_system_id: '',
    target_system_name: '',
    customization_type: 'Enhancement',
    is_error_ticket: false,
    error_code: '',
    error_message: '',
    error_module: '',
    chain_of_thought: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchConfiguredSystems();
  }, []);

  const fetchConfiguredSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-systems`);
      setConfiguredSystems(response.data);
    } catch (error) {
      console.error('Error fetching configured systems:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`);
      setTickets(response.data);
      
      // Fetch assigned agents for each ticket
      const agentPromises = response.data.map(async (ticket) => {
        try {
          const agentsRes = await axios.get(`${API}/agent-studio/tickets/${ticket.ticket_number}/agents`);
          return { ticketId: ticket.id, agents: agentsRes.data };
        } catch (err) {
          return { ticketId: ticket.id, agents: [] };
        }
      });
      
      const agentsData = await Promise.all(agentPromises);
      const agentsMap = {};
      agentsData.forEach(({ ticketId, agents }) => {
        agentsMap[ticketId] = agents;
      });
      setAssignedAgentsMap(agentsMap);
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };


  const createMockTicket = async () => {
    try {
      const response = await axios.post(`${API}/tickets/mock`);
      console.log('Mock ticket created:', response.data);
      alert(`✅ Mock ticket created: ${response.data.ticket_number}`);
      await fetchTickets();
    } catch (error) {
      console.error('Error creating mock ticket:', error);
      alert(`❌ Error creating mock ticket: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createTicket = async () => {
    try {
      await axios.post(`${API}/tickets`, formData);
      setOpen(false);
      fetchTickets();
      setFormData({
        ticket_number: '',
        source: 'local',
        title: '',
        description: '',
        priority: 'medium',
        requester_name: '',
        requester_email: '',
        sap_system: 'S/4HANA Cloud',
        target_system_id: '',
        target_system_name: '',
        customization_type: 'Enhancement',
        is_error_ticket: false,
        error_code: '',
        error_message: '',
        error_module: '',
        chain_of_thought: ''
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleSystemSelection = (systemId) => {
    const selectedSystem = configuredSystems.find(s => s.id === systemId);
    setFormData({
      ...formData,
      target_system_id: systemId,
      target_system_name: selectedSystem ? selectedSystem.name : '',
      sap_system: selectedSystem ? selectedSystem.type : formData.sap_system
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-gray-500',
      'in_analysis': 'bg-blue-500',
      'in_design': 'bg-purple-500',
      'in_development': 'bg-indigo-500',
      'in_testing': 'bg-amber-500',
      'in_deployment': 'bg-orange-500',
      'in_governance': 'bg-cyan-500',
      'completed': 'bg-green-500',
      'rejected': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'border-gray-400 text-gray-600',
      'medium': 'border-blue-400 text-blue-600',
      'high': 'border-orange-400 text-orange-600',
      'critical': 'border-red-400 text-red-600'
    };
    return colors[priority] || 'border-gray-400 text-gray-600';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
          <p className="text-muted-foreground mt-2">Manage and View all tickets</p>
        </div>
      </div>

      <div className="mt-4">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4">No tickets found. Create a new ticket to get started.</p>
                <div className="flex gap-2">
                  <Button onClick={createMockTicket}>Generate Mock Ticket</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tickets.map((ticket) => (
                <Card key={ticket.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/tickets/${ticket.ticket_number}`)} data-testid={`ticket-card-${ticket.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                          <Badge variant="outline" className="text-xs">{ticket.source}</Badge>
                          <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>{ticket.status.replace('_', ' ')}</Badge>
                          <Badge variant="outline" className={`text-xs border ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                        </div>
                        <CardTitle className="text-xl">{ticket.title}</CardTitle>
                        <CardDescription className="mt-2">{ticket.description}</CardDescription>
                      </div>
                      <ExternalLink className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Target System</div>
                          <div className="font-medium">
                            {ticket.target_system_name || ticket.sap_system}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Type</div>
                          <div className="font-medium">{ticket.customization_type}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Requester</div>
                          <div className="font-medium">{ticket.requester_name}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Created</div>
                          <div className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      
                      {/* Assigned Agents from Agent Studio */}
                      {assignedAgentsMap[ticket.id] && assignedAgentsMap[ticket.id].length > 0 && (
                        <div className="pt-2 border-t">
                          <div className="text-xs text-muted-foreground mb-1">Custom Agents:</div>
                          <div className="flex flex-wrap gap-1">
                            {assignedAgentsMap[ticket.id].map((assignment, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                <Sparkles className="h-3 w-3 mr-1" />
                                {assignment.agent_name}
                                {assignment.status === 'completed' && ' ✓'}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
